// src/hooks/useAppState.js
import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { OILS, SKUS, DEFAULT_YIELDS } from "../constants";

const initState = () => ({
  rawStock:  Object.fromEntries(OILS.map(o => [o, 0])),
  oilPlant:  Object.fromEntries(OILS.map(o => [o, 0])),
  oilShop:   Object.fromEntries(OILS.map(o => [o, 0])),
  cakeStock: Object.fromEntries(OILS.map(o => [o, 0])),
  prices: {
    raw:  Object.fromEntries(OILS.map(o => [o, 80])),
    cake: Object.fromEntries(OILS.map(o => [o, 30])),
    oil:  Object.fromEntries(OILS.map(o => [o, Object.fromEntries(SKUS.map(s => [s, 0]))])),
  },
  yields: { ...DEFAULT_YIELDS },
  purchases:   [],
  batches:     [],
  transfers:   [],
  sales:       [],
  cakeSales:   [],
  collections: [],
});

export function useAppState() {
  const [state, setState] = useState(initState);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  // Offline queue: operations that failed to save to server
  const [offlineQueue, setOfflineQueue] = useState([]);
  const syncTimer = useRef(null);

  // Load all data from Google Sheets on mount
  const loadAll = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      const data = await api.getAll();
      setState(prev => ({
        ...prev,
        rawStock:   data.rawStock   || prev.rawStock,
        oilPlant:   data.oilPlant   || prev.oilPlant,
        oilShop:    data.oilShop    || prev.oilShop,
        cakeStock:  data.cakeStock  || prev.cakeStock,
        prices:     data.prices     || prev.prices,
        yields:     data.yields     || prev.yields,
        purchases:  data.purchases  || prev.purchases,
        batches:    data.batches    || prev.batches,
        transfers:  data.transfers  || prev.transfers,
        sales:      data.sales      || prev.sales,
        cakeSales:  data.cakeSales  || prev.cakeSales,
        collections: data.collections || prev.collections,
      }));
      setLastSync(new Date());
    } catch (err) {
      setError("Could not connect to Google Sheets. Working in offline mode.");
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    // Auto-refresh every 2 minutes to pick up changes from other users
    syncTimer.current = setInterval(loadAll, 2 * 60 * 1000);
    return () => clearInterval(syncTimer.current);
  }, [loadAll]);

  // Generic optimistic update helper with server validation
  // Applies change locally first, then syncs to server
  // If server rejects, rolls back local state and shows error
  const optimisticUpdate = useCallback(async (localUpdate, apiCall, rollback) => {
    const prevState = await new Promise(resolve => {
      setState(prev => { resolve(prev); return localUpdate(prev); });
    });
    setSyncing(true);
    try {
      const result = await apiCall();
      // Server returned an error (stock validation failed)
      if (result && result.error) {
        setState(prevState); // roll back
        throw new Error(result.error);
      }
      setLastSync(new Date());
      return { success: true };
    } catch (err) {
      // If it's a stock validation error, roll back and show clearly
      if (err.message && !err.message.includes("fetch")) {
        setState(prevState);
        setError(err.message);
        setTimeout(() => setError(null), 5000);
        return { success: false, error: err.message };
      }
      setError("Saved locally. Will sync when connection is restored.");
      setOfflineQueue(q => [...q, apiCall]);
      return { success: true };
    } finally {
      setSyncing(false);
    }
  }, []);

  // ── ACTION HANDLERS ─────────────────────────────────────────────

  const addPurchase = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    return await optimisticUpdate(
      prev => ({
        ...prev,
        rawStock: { ...prev.rawStock, [data.oil]: prev.rawStock[data.oil] + data.kgs },
        prices: { ...prev.prices, raw: { ...prev.prices.raw, [data.oil]: data.price } },
        purchases: [...prev.purchases, entry],
      }),
      () => api.purchase(entry)
    );
  }, [optimisticUpdate]);

  const addBatch = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    return await optimisticUpdate(
      prev => ({
        ...prev,
        rawStock:  { ...prev.rawStock,  [data.oil]: prev.rawStock[data.oil]  - data.rawUsed   },
        oilPlant:  { ...prev.oilPlant,  [data.oil]: prev.oilPlant[data.oil]  + data.oilLitres },
        cakeStock: { ...prev.cakeStock, [data.oil]: prev.cakeStock[data.oil] + data.cakeKg    },
        batches: [...prev.batches, entry],
      }),
      () => api.batch(entry)
    );
  }, [optimisticUpdate]);

  const addTransfer = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    return await optimisticUpdate(
      prev => ({
        ...prev,
        oilPlant: { ...prev.oilPlant, [data.oil]: prev.oilPlant[data.oil] - data.litres },
        oilShop:  { ...prev.oilShop,  [data.oil]: prev.oilShop[data.oil]  + data.litres },
        transfers: [...prev.transfers, entry],
      }),
      () => api.transfer(entry)
    );
  }, [optimisticUpdate]);

  const addSale = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    return await optimisticUpdate(
      prev => {
        const ns = { ...prev };
        if (data.location === "Shop") {
          ns.oilShop  = { ...ns.oilShop,  [data.oil]: ns.oilShop[data.oil]  - data.litres };
        } else {
          ns.oilPlant = { ...ns.oilPlant, [data.oil]: ns.oilPlant[data.oil] - data.litres };
        }
        ns.sales = [...ns.sales, entry];
        return ns;
      },
      () => api.sale(entry)
    );
  }, [optimisticUpdate]);

  const addCakeSale = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    return await optimisticUpdate(
      prev => ({
        ...prev,
        cakeStock: { ...prev.cakeStock, [data.oil]: prev.cakeStock[data.oil] - data.kg },
        cakeSales: [...prev.cakeSales, entry],
      }),
      () => api.cakeSale(entry)
    );
  }, [optimisticUpdate]);

  const updateYields = useCallback(async (oil, oilPct, cakePct, user) => {
    await optimisticUpdate(
      prev => ({
        ...prev,
        yields: { ...prev.yields, [oil]: { oil: oilPct, cake: cakePct } },
      }),
      () => api.updateYields({ oil, oilPct, cakePct, by: user.name })
    );
  }, [optimisticUpdate]);

  const updatePrices = useCallback(async (newPrices, user) => {
    await optimisticUpdate(
      prev => ({ ...prev, prices: newPrices }),
      () => api.updatePrices({ prices: newPrices, by: user.name })
    );
  }, [optimisticUpdate]);

  // ── EDIT ENTRY ─────────────────────────────────────────────────────────────
  const editEntry = useCallback(async (updated, type, user) => {
    const typeMap = {
      sales: "sale", purchases: "purchase", batches: "batch",
      transfers: "transfer", cakeSales: "cakeSale",
    };
    const entryType = typeMap[type] || type;

    await optimisticUpdate(
      prev => {
        const ns = { ...prev };
        const listKey = type;
        ns[listKey] = ns[listKey].map(e => e.ts === updated.ts ? updated : e);
        return ns;
      },
      () => api.editEntry({ type: entryType, ts: updated.ts, updatedData: updated, by: user.name })
    );
  }, [optimisticUpdate]);

  // ── DELETE ENTRY ────────────────────────────────────────────────────────────
  const deleteEntry = useCallback(async (entry, type, user) => {
    const typeMap = {
      sales: "sale", purchases: "purchase", batches: "batch",
      transfers: "transfer", cakeSales: "cakeSale",
    };
    const entryType = typeMap[type] || type;

    await optimisticUpdate(
      prev => {
        const ns = { ...prev };
        // Remove from list
        ns[type] = ns[type].filter(e => e.ts !== entry.ts);
        // Reverse the stock effect
        if (type === "purchases") {
          ns.rawStock = { ...ns.rawStock, [entry.oil]: ns.rawStock[entry.oil] - Number(entry.kgs) };
        }
        if (type === "batches") {
          ns.rawStock  = { ...ns.rawStock,  [entry.oil]: ns.rawStock[entry.oil]  + Number(entry.rawUsed)   };
          ns.oilPlant  = { ...ns.oilPlant,  [entry.oil]: ns.oilPlant[entry.oil]  - Number(entry.oilLitres) };
          ns.cakeStock = { ...ns.cakeStock, [entry.oil]: ns.cakeStock[entry.oil] - Number(entry.cakeKg)    };
        }
        if (type === "transfers") {
          ns.oilPlant = { ...ns.oilPlant, [entry.oil]: ns.oilPlant[entry.oil] + Number(entry.litres) };
          ns.oilShop  = { ...ns.oilShop,  [entry.oil]: ns.oilShop[entry.oil]  - Number(entry.litres) };
        }
        if (type === "sales") {
          if (entry.location === "Shop") ns.oilShop  = { ...ns.oilShop,  [entry.oil]: ns.oilShop[entry.oil]  + Number(entry.litres) };
          else                           ns.oilPlant = { ...ns.oilPlant, [entry.oil]: ns.oilPlant[entry.oil] + Number(entry.litres) };
        }
        if (type === "cakeSales") {
          ns.cakeStock = { ...ns.cakeStock, [entry.oil]: ns.cakeStock[entry.oil] + Number(entry.kg) };
        }
        return ns;
      },
      () => api.deleteEntry({ type: entryType, ts: entry.ts, by: user.name })
    );
  }, [optimisticUpdate]);

  // ── ADD COLLECTION (credit payment received) ────────────────────────────────
  const addCollection = useCallback(async (data, user) => {
    const entry = { ...data, ts: Date.now(), by: user.name };
    await optimisticUpdate(
      prev => ({
        ...prev,
        collections: [...(prev.collections || []), entry],
      }),
      () => api.addCollection(entry)
    );
  }, [optimisticUpdate]);

  // ── OPENING STOCK ──────────────────────────────────────────────────────────  
const addOpeningStock = useCallback(async (entries, user) => {
    await optimisticUpdate(
      prev => {
        const ns = { ...prev };
        for (const e of entries) {
          ns.rawStock  = { ...ns.rawStock,  [e.oil]: (ns.rawStock[e.oil]  || 0) + e.rawKg     };
          ns.oilPlant  = { ...ns.oilPlant,  [e.oil]: (ns.oilPlant[e.oil]  || 0) + e.oilPlantL };
          ns.oilShop   = { ...ns.oilShop,   [e.oil]: (ns.oilShop[e.oil]   || 0) + e.oilShopL  };
          ns.cakeStock = { ...ns.cakeStock, [e.oil]: (ns.cakeStock[e.oil] || 0) + e.cakeKg    };
        }
        return ns;
      },
      () => api.addOpeningStock({ entries, by: user.name })
    );
  }, [optimisticUpdate]);

  return {
    state,
    loading,
    syncing,
    lastSync,
    error,
    offlineQueue: offlineQueue.length,
    refresh: loadAll,
    actions: {
      addPurchase,
      addBatch,
      addTransfer,
      addSale,
      addCakeSale,
      updateYields,
      updatePrices,
      editEntry,
      deleteEntry,
      addOpeningStock,
      addCollection,
    },
  };
}
